import { type TFunction } from 'i18next'

import { assertExists, exists } from '~/utils'

import { pathLabelI18nKeyMap } from '../../../routes/paths'
import { type CustomLabels, resolveOutputTypeLabel } from '../atoms'

import { kindToPathKey, type RenderTask } from './planRenderTasks'

// Build a DocumentImage title in the `A - B - C - F - D - E` convention:
//   A = brand (standard) or brand-group name (customer/customized)
//   B = feature name, with the segment view in parens (customized is 9segs-only)
//   C = wizard question/output-type label (absent for customer → collapses)
//   F = 自社 framing brand (group-wide copies only)
//   D = filter pattern name
//   E = survey title
// Absent fields collapse via filter(Boolean) so no empty ` - ` separators remain.
export const formatDocumentImageLabel = (args: {
  task: RenderTask
  brandGroupName?: string
  surveyTitle: string
  customLabels: CustomLabels
  t: TFunction
}): string => {
  const { brandGroupName, customLabels, surveyTitle, t, task } = args

  // A: standard tasks carry a brand (brandId set ⇒ brandName set by construction,
  // so assert rather than cast); customer/customized show the group name, which
  // collapses to '' when undefined (never the 'all' structural sentinel).
  let a: string
  if (exists(task.brandId)) {
    assertExists(task.brandName)
    a = task.brandName
  } else {
    a = brandGroupName ?? ''
  }

  // B: feature name; standard/customer append the segment view, customized is
  // 9segs-only so it carries no paren.
  const feature = t(pathLabelI18nKeyMap[kindToPathKey[task.kind]])
  const b =
    task.kind === 'customized'
      ? feature
      : `${feature}(${t(task.segmentMode === '5segs' ? 'five_segs' : 'nine_segs')})`

  // C: bare role label, identical for standard & customized (a customizedAnalysis_
  // key would re-prefix カスタマイズ分析, duplicating B). Customer has no role → ''.
  const c = task.role
    ? resolveOutputTypeLabel(`standardAnalysis_${task.role}`, customLabels, t)
    : ''

  // F: without this every 自社 copy of a group-wide table shares one title (A is the
  // group name, B/C/D/E are identical), leaving them indistinguishable in the
  // saved-figures list. Phrased as 自社: <name> so it does not read as "this brand's
  // table" — the grid still holds every brand in the group.
  const f = task.framingBrandName
    ? t('basic_output_own_brand_perspective', {
        name: task.framingBrandName,
      })
    : ''

  const d = task.filterName
  const e = surveyTitle

  return [a, b, c, f, d, e].filter(Boolean).join(' - ')
}
